import { useEffect, useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useChauffeurSocket from "@/hooks/useChauffeurSocket";

/**
 * PickupPinReveal — passenger-side widget that shows the 4-digit PIN
 * the driver must enter at pickup. Each digit gets its own card so it's
 * easy to read aloud over the phone or in person.
 *
 * Once verified, swaps to a green confirmation badge with a checkmark.
 *
 * Hidden when ride is completed/cancelled or before driver assigned.
 */
export default function PickupPinReveal({ ride }) {
  const [pin, setPin] = useState(null);
  const [verified, setVerified] = useState(Boolean(ride?.pickup_pin_verified));
  const [loading, setLoading] = useState(true);
  const { on } = useChauffeurSocket({ rideId: ride?.id });

  const shouldRender = ride && ride.driver_email
    && ride.status !== "completed" && ride.status !== "cancelled";

  useEffect(() => {
    if (!shouldRender) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/chauffeur/rides/${ride.id}/pickup-pin`);
        if (cancelled) return;
        setPin(res.data?.pin || null);
        setVerified(Boolean(res.data?.verified));
      } catch {
        // 403/404 — non-fatal, just hide
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ride?.id, shouldRender]);

  useEffect(() => {
    if (!ride?.id) return;
    const off = on("pickup_pin_verified", (payload) => {
      if (payload.ride_id !== ride.id) return;
      setVerified(true);
      toast.success("PIN verified — your ride is starting");
    });
    return () => off();
  }, [ride?.id, on]);

  if (!shouldRender) return null;

  if (verified) {
    return (
      <div data-testid="pickup-pin-verified"
        className="flex items-center justify-between p-3 border border-emerald-400/30 bg-emerald-500/10 rounded-md">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-300" />
          <p className="text-[11px] uppercase tracking-wider text-emerald-300">PIN verified · ride started</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pickup-pin-reveal"
      className="p-3 border border-gold/30 bg-gold/[0.05] rounded-md space-y-2">
      <div className="flex items-center gap-2">
        <Lock size={14} className="text-gold" />
        <p className="text-[11px] uppercase tracking-wider text-gold">Pickup PIN · read to driver</p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-xs">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : !pin ? (
        <p className="text-[11px] text-white/40">PIN not available yet.</p>
      ) : (
        <div className="flex items-center gap-2">
          {pin.split("").map((d, i) => (
            <div key={i} data-testid={`pin-digit-${i}`}
              className="w-12 h-14 flex items-center justify-center bg-black/50 border border-gold/40 rounded text-2xl font-display text-gold">
              {d}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-white/40">Confirms the driver picked up the right rider — every ride.</p>
    </div>
  );
}
